import { QueryClient, useQuery } from '@tanstack/react-query';
import request from '@utils/request';
import { SpaEntityType } from 'shared-types/Spa';

/** Ensure minimumGuests is at least 1 for any available treatment */
const normalizeSpaTreatment = (treatment: SpaEntityType): SpaEntityType => ({
  ...treatment,
  minimumGuests: Math.max(1, treatment.minimumGuests ?? 1),
});

export const getSpaTreatment = async (
  propertyId: number,
  language: string,
  spaTreatmentId: any,
  params: {
    arrivalDate: string | null;
    isTimeSlot?: number;
    numberOfGuests?: number;
    showUnpublished?: number;
  }
) => {
  const { data } = await request.get(`${propertyId}/spa-treatments/${spaTreatmentId}/${language}`, {
    params,
  });
  return normalizeSpaTreatment(data as SpaEntityType);
};

export const getSpaTreatmentPrefetch = async (
  queryClient: QueryClient,
  propertyId: number,
  language: string,
  spaTreatmentId: any,
  arrivalDate: string | null,
  numberOfGuests?: number
) => {
  await queryClient.prefetchQuery(
    getAvailableSpaTreatmentKey(
      propertyId,
      language,
      spaTreatmentId,
      { arrivalDate, numberOfGuests },
      false
    ),
    () =>
      getSpaTreatment(propertyId, language, spaTreatmentId, {
        arrivalDate,
        isTimeSlot: arrivalDate ? 1 : undefined,
        ...(numberOfGuests !== undefined ? { numberOfGuests } : {}),
      }),
    { cacheTime: Infinity, staleTime: Infinity }
  );
};

export const useGetSpaTreatment = (
  propertyId: number,
  language: string,
  spaTreatmentId: any,
  arrivalDate: string | null,
  enabled = true,
  showUnpublished = false,
  numberOfGuests?: number
) => {
  const { data, isFetching } = useQuery({
    queryKey: getAvailableSpaTreatmentKey(
      propertyId,
      language,
      spaTreatmentId,
      { arrivalDate, numberOfGuests },
      showUnpublished
    ),
    queryFn: () =>
      getSpaTreatment(propertyId, language, spaTreatmentId, {
        arrivalDate,
        isTimeSlot: arrivalDate ? 1 : undefined,
        ...(numberOfGuests !== undefined ? { numberOfGuests } : {}),
        ...(showUnpublished ? { showUnpublished: 1 } : {}),
      }),
    cacheTime: Infinity,
    staleTime: Infinity,
    keepPreviousData: true,
    enabled,
  });
  return { data, isFetching };
};

export const getAvailableSpaTreatmentKey = (
  propertyId: number,
  language: string,
  spaTreatmentId: any,
  params: {
    arrivalDate: string | null;
    numberOfGuests?: number;
  },
  showUnpublished: boolean
) => [
  'spaTreatment',
  'available',
  propertyId,
  parseInt(spaTreatmentId, 10),
  ...Object.values(params),
  language,
  showUnpublished,
];
